export type Role = "buyer" | "seller" | "both";
export type AppMode = "buyer" | "seller";

const STORAGE_KEY = "marketlink.mode";

export function getAllowedModes(role?: string | null): AppMode[] {
  if (role === "seller") return ["seller"];
  if (role === "both") return ["buyer", "seller"];
  return ["buyer"];
}

export function isOnboardingComplete(user: {
  roles?: string | null;
  zipCode?: string | null;
  onboardingCompletedAt?: string | Date | null;
} | null | undefined) {
  return Boolean(
    user &&
      user.roles &&
      ["buyer", "seller", "both"].includes(user.roles) &&
      user.zipCode &&
      String(user.zipCode).trim() &&
      user.onboardingCompletedAt
  );
}

export function canSell(user: { roles?: string | null } | null | undefined) {
  return user?.roles === "seller" || user?.roles === "both";
}

// Whether the user has confirmed their email address. Drives the "verify your
// email" gate that sits in front of onboarding for password-based signups.
// OAuth accounts are stamped verified server-side at creation.
export function isEmailVerified(user: {
  emailVerifiedAt?: string | Date | null;
} | null | undefined) {
  return Boolean(user && user.emailVerifiedAt);
}

export function readStoredMode(): AppMode | null {
  if (typeof window === "undefined") return null;
  const value = window.localStorage.getItem(STORAGE_KEY);
  return value === "buyer" || value === "seller" ? value : null;
}

export function persistMode(mode: AppMode) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(STORAGE_KEY, mode);
}

export function resolveMode(role?: string | null): AppMode {
  const allowed = getAllowedModes(role);
  const stored = readStoredMode();

  if (stored && allowed.includes(stored)) return stored;
  return allowed.includes("buyer") ? "buyer" : "seller";
}

export function getDefaultRouteForMode(mode: AppMode) {
  return mode === "seller" ? "/seller" : "/";
}

export function getDefaultRouteForUser(user: {
  roles?: string | null;
  zipCode?: string | null;
  onboardingCompletedAt?: string | Date | null;
} | null | undefined) {
  if (!user) return "/";
  if (!isOnboardingComplete(user)) return "/onboarding";
  return getDefaultRouteForMode(resolveMode(user.roles));
}
